import type { Question } from '@mentimeter/legacy-series-types/question';
import type {
  EditorQuestionWithSlide,
  InteractiveContent,
} from '../../compatibility-overrides/editor-types';
import type { Slide as VotingSlide } from '../../compatibility-overrides/voting-types';
import type { CompatibilitySlide } from '../../compatibility-types';
import {
  DEFAULT_RESPONSE_RANGE,
  RESPONSE_POLICY_SINGLE_RESPONSE_ALL_CHOICES,
} from '../../constants';
import { getOrSetCache, type WithCache } from '../../utils/get-or-set-cache';
import {
  ratingAxisLabelsToQuestionDimensions,
  ratingGridToQuestionGrid,
} from '../converters/interactive-content';
import type { CommonQuestionProperty } from './get-common-question-property';

export const getQuestionRatingProperty = (
  target: CompatibilitySlide<'rating'> | VotingSlide<'rating'>,
  prop: keyof Question,
) => {
  const interactiveContent = target.interactiveContents?.[0];

  const staticContent = target.staticContent;
  const interactiveContentsStyling = staticContent.interactiveContentStyling;

  if (prop === 'question_styled') {
    return staticContent.styledTitle ?? {};
  }

  if (interactiveContent) {
    switch (prop as keyof Omit<Question, CommonQuestionProperty>) {
      case 'hide_skip':
        return (
          interactiveContent.responsePolicy ===
          RESPONSE_POLICY_SINGLE_RESPONSE_ALL_CHOICES
        );
      case 'dimensions':
        const dimensions = ratingAxisLabelsToQuestionDimensions({
          xAxisLabels: interactiveContentsStyling.xAxisLabels,
          yAxisLabels: interactiveContentsStyling.yAxisLabels,
        });

        const cacheKey = JSON.stringify(dimensions);
        return getOrSetCache(
          target as WithCache<EditorQuestionWithSlide>,
          'dimensions',
          cacheKey,
          dimensions,
        );
      case 'range':
        return interactiveContent.responseRange ?? DEFAULT_RESPONSE_RANGE;
      case 'grid':
        const grid = ratingGridToQuestionGrid(
          interactiveContent as InteractiveContent,
          staticContent,
        );

        return getOrSetCache(
          target as WithCache<CompatibilitySlide>,
          'grid',
          JSON.stringify(grid),
          grid,
        );
      default:
        return undefined;
    }
  }
};
